import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  AssetLoader,
  AudioBus,
  BingoDemoScene,
  BoardDemoScene,
  ChestOpenScene,
  CrashDemoScene,
  DailyQuestScene,
  HigherLowerDemoScene,
  PickEmDemoScene,
  PlayerProfile,
  PlinkoDemoScene,
  ScratchDemoScene,
  SlotDemoScene,
  WheelDemoScene,
  type SlotLayoutBounds,
  type SlotGambleFn,
  type SlotSpinFn,
  type SlotSettingsHost,
  type Theme,
} from '@goodtime/engine';
import { slotBetFor, type SpinSlotResponse } from '@goodtime/shared';
import { SceneLoadingOverlay } from './SceneLoadingOverlay';

export type SceneKind =
  | 'slot'
  | 'wheel'
  | 'bingo'
  | 'board'
  | 'pickem'
  | 'crash'
  | 'plinko'
  | 'higherlower'
  | 'scratch'
  | 'chest'
  | 'daily';

interface PixiScene {
  init(): Promise<void>;
  destroy(): void;
}

/** Asset-load gate. The Pixi scene is only constructed once
 * {@link LoadState.Ready} so its lifecycle never overlaps an
 * in-flight {@code AssetLoader.loadTheme} — a StrictMode
 * mount→unmount→remount during slow video preloads can otherwise
 * strand a half-initialised {@code Pixi.Application} (see
 * decision-0008). */
type LoadState = 'loading' | 'ready' | 'error';

/**
 * Hosts a PixiJS scene inside a React component lifecycle.
 *
 * <p>Two-phase mount: theme assets preload via the AssetLoader +
 * AudioBus singletons first, then the active Pixi scene constructs
 * once everything is in cache. The scene's own canvas appends into
 * {@code containerRef}; every mount gets a fresh canvas + WebGL
 * context, and swapping {@code scene} or {@code theme} re-runs both
 * phases.
 *
 * <p>The {@link PlayerProfile} stays stable across switches so the
 * wallet carries between games (Phase G.1).
 *
 * <p>Phase D.1 — when {@code slotSpinFn} is set, the slot scene runs
 * in server-authoritative mode (every spin calls
 * {@code POST /sessions/{id}/spin} and the wallet balance comes from
 * the response). Null means offline / local-only — the engine falls
 * back to its built-in {@link RunSpinLogic}. A slot built while it is
 * null switches to server spins between spins once it arrives.
 */
export function CanvasHost({
  scene,
  theme,
  profile,
  slotSpinFn,
  slotGambleFn,
  slotBetLadder,
  onSlotSpinFired,
  onSlotServerSpinLanded,
  onSlotGambleOpenChange,
  onSlotCanvasPopupChange,
  onSlotSceneReady,
  onSlotServerModeChange,
  onSceneShown,
  onboardingSession,
  onSceneComplete,
  onOpenSettings,
  slotSettingsHost,
  gambleEnabled = true,
  onSlotLayoutBounds,
  showLoadoutStrip = false,
  loadoutSlots,
  loadoutPendingIcon,
  loadoutPendingLabel,
  loadoutHidden,
  onLoadoutTileClick,
  onInfoModalToggle,
  walletAnchors,
  slotNodeId,
  road = false,
  onBoardStopPressed,
  onBoardCollectPressed,
  onSlotIdle,
}: {
  scene: SceneKind;
  theme: Theme;
  profile: PlayerProfile;
  slotSpinFn?: SlotSpinFn | null;
  /** Gambles a server spin's win on the server (double-or-nothing). Null
   *  means the gamble is drawn on the device. */
  slotGambleFn?: SlotGambleFn | null;
  /** The single-player slot: offer the slot's bet ladder (its bet times 1,
   *  2, 4 or 8), start at the player's last choice, and show the paytable at
   *  the server's pay scale. Off for the Reel Race. */
  slotBetLadder?: boolean;
  /** Fired once per slot spin commit. Used by the multiplayer Reel
   * Race host to submit a server-side REEL_RACE_SPIN command in
   * lockstep with the local slot's visual spin. */
  onSlotSpinFired?: ((bet: number) => void) | null;
  /** Server-mode slot: fired when the reels land, with the spin response
   *  (the Sweet Trail panel moves on this). */
  onSlotServerSpinLanded?: ((response: SpinSlotResponse) => void) | null;
  /** True while the slot's double-or-nothing overlay is open. */
  onSlotGambleOpenChange?: ((open: boolean) => void) | null;
  /** True while a bonus or pop-up drawn on the slot's canvas covers it
   *  (hold and spin): overlays above the canvas hide until it closes. */
  onSlotCanvasPopupChange?: ((open: boolean) => void) | null;
  /** Fired with the constructed SlotDemoScene reference once the
   * scene is mounted, and again with {@code null} on unmount. Lets
   * a host component (e.g. {@code WheelReelRaceView}) call into
   * the scene for orchestrated effects like
   * {@link SlotDemoScene#playItemDropReveal} without re-importing
   * the canvas. Only fires for the {@code 'slot'} scene kind. */
  onSlotSceneReady?: ((instance: SlotDemoScene | null) => void) | null;
  /** Fired when a slot that opened before {@code slotSpinFn} was set
   *  switches its spins to the server. */
  onSlotServerModeChange?: ((serverMode: boolean) => void) | null;
  /** Whether the scene is on screen: false as a scene starts building
   * (the loading card goes up) or the host unmounts, true once it has
   * finished init() and the loading card has come down. */
  onSceneShown?: ((shown: boolean) => void) | null;
  /** F2.2 — onboarding-curriculum session number (UX Spec §4). When 1,
   * the slot scene mounts with bet locked at 25 and the BET selector
   * hidden. Null/undefined = standard interactive selector. */
  onboardingSession?: number | null;
  /** Single-shot scenes (scratch, chest, daily) call this when the player
   * taps CLAIM after a reward resolves — App typically routes back to the
   * world map. Long-running scenes (slot, wheel, bingo, …) ignore it. */
  onSceneComplete?: () => void;
  /** Phase 4 settings cog — the slot HUD includes a gear button to
   *  the left of INFO; tapping it invokes this callback. The host
   *  typically toggles a SettingsModal. Omit to hide the gear. */
  onOpenSettings?: () => void;
  /** The standalone slot's own settings board: what only the host can do
   *  (notifications, restoring purchases). Read when the gear is tapped. */
  slotSettingsHost?: SlotSettingsHost | null;
  /** When {@code false}, the slot scene's post-win gamble offer is
   *  suppressed — wins collect immediately. Defaults to {@code true};
   *  the multiplayer Reel Race passes {@code false} so gamble doesn't
   *  interrupt the race-to-collect rhythm. */
  gambleEnabled?: boolean;
  /** Royal Road: the slot runs with a fixed bet, side lights and Lamp
   *  Runs. Only for flagged players on a road slot. */
  road?: boolean;
  /** Royal Road: SPIN (or Space) pressed while it reads STOP. */
  onBoardStopPressed?: (() => void) | null;
  /** Royal Road: SPIN (or Space) pressed while it reads COLLECT or DONE. */
  onBoardCollectPressed?: (() => void) | null;
  /** The slot finished a spin (wins and bonuses included) and is waiting. */
  onSlotIdle?: (() => void) | null;
  /** Fires on every slot-scene layout pass with the frame bounds in
   *  canvas-internal pixel coordinates. Lets a React overlay (e.g.
   *  the Reel-Race loadout strip) position itself precisely
   *  alongside the Pixi HUD pills. Only fires for the
   *  {@code 'slot'} scene kind. */
  onSlotLayoutBounds?: (bounds: SlotLayoutBounds) => void;
  /** Multiplayer Reel Race — mount the 3-slot loadout strip inside
   *  the slot HUD row, to the LEFT of the gear/info cluster. */
  showLoadoutStrip?: boolean;
  /** Slot icons currently in each of the 3 loadout slots. Each entry
   *  is a theme asset alias (e.g. {@code "item:BOOST"}) or null for
   *  an empty slot. */
  loadoutSlots?: ReadonlyArray<string | null>;
  /** Pending-claim incoming item — sets the badge below the strip
   *  and starts the gold pulse on the 3 tiles. */
  loadoutPendingIcon?: string | null;
  /** Human-readable name shown in the "TAP TO REPLACE <Name>" pill
   *  when {@link loadoutPendingIcon} is non-null. */
  loadoutPendingLabel?: string;
  /** True while the local player is mid-spin — fades the strip out
   *  with the scene's ticker-driven fade. */
  loadoutHidden?: boolean;
  /** Tile-tap handler — fires with the 0-based slot index. */
  onLoadoutTileClick?: (slotIndex: number) => void;
  /** Producer brief 2026-05-15 — fires when the slot's info modal
   *  (paytable or items tab) opens or closes. The Reel Race host
   *  uses this to hide the React overlays (countdown, leaderboard)
   *  that would otherwise paint above the canvas-rendered dialog. */
  onInfoModalToggle?: (open: boolean) => void;
  /** Position bridge — live coin + gem chip centres (viewport CSS px)
   *  measured on the React BalancePill, forwarded to the slot scene so the
   *  take-win flies to the real chips instead of a hardcoded offset that
   *  drifts when the pill's layout changes. Null = use computed targets. */
  walletAnchors?: {
    coin: { x: number; y: number };
    gem: { x: number; y: number };
  } | null;
  /** Slot mastery (M1, 2026-06-26) — world-node id of the slot being
   *  played, forwarded to the slot scene so each paid spin's bet records
   *  against that slot's cumulative-wagered stars. Null for non-world
   *  entries (multiplayer reel race, dev harness). */
  slotNodeId?: number | null;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loadState, setLoadState] = useState<LoadState>('loading');
  // First-spin art download progress (0..1) for the slot's loading card;
  // null for scenes whose load isn't measured.
  const [loadProgress, setLoadProgress] = useState<number | null>(null);
  // True once the built scene has finished init() and is drawing; the
  // loading card stays up until then so the canvas never shows empty.
  const [sceneShown, setSceneShown] = useState<boolean>(false);
  // The theme id phase-1 has finished preloading. Guards the scene
  // build (phase 2) against a theme-CHANGE race: when {@code theme}
  // changes, phase-1 queues setLoadState('loading') but the build
  // effect runs in the SAME commit and would otherwise read the stale
  // {@code loadState === 'ready'} and rebuild the scene against the new
  // theme before its textures/bg have loaded — leaving the slot on
  // emoji-glyph + gradient fallbacks. Gating the build on
  // {@code readyThemeId === theme.id} blocks that premature rebuild;
  // the build re-fires once phase-1 marks the NEW theme ready. Surfaced
  // by the 2026-06-06 multiplayer reel-skin selector (the first live
  // theme swap on a mounted scene).
  const [readyThemeId, setReadyThemeId] = useState<string | null>(null);

  // Stash mutable callbacks in refs so the scene doesn't re-init when
  // their identity changes between parent renders. Without this, every
  // React state change in the host (e.g. an SSE-driven leaderboard
  // tick) creates a new arrow-function for these callbacks, which
  // triggers the useEffect's cleanup (scene.destroy()) and re-init —
  // tearing down a mid-spin canvas in the process.
  //
  // Producer-audit Phase 1.1 (May 2026): adding onSceneComplete to the
  // ref-stashed set was the missing link — App.tsx was passing
  // {@code () => setView('map')} as an inline arrow. Every SSE tick
  // (one per second from the room heartbeat) re-rendered App, minted
  // a fresh arrow, the deps array (line below) saw the identity change,
  // and the entire scene tree was thrown out + rebuilt — manifesting as
  // brief black flashes, "lost" click handlers (re-bound to a
  // freshly-mounted scene the click had already missed), bets that
  // never deducted (model state torn down before persist), and the
  // "X ticks · 1 listening" telemetry climbing in lockstep with the
  // resets.
  const slotSpinFnRef = useRef(slotSpinFn);
  const slotGambleFnRef = useRef(slotGambleFn);
  slotGambleFnRef.current = slotGambleFn;
  const onSlotSpinFiredRef = useRef(onSlotSpinFired);
  const onSlotServerSpinLandedRef = useRef(onSlotServerSpinLanded);
  onSlotServerSpinLandedRef.current = onSlotServerSpinLanded;
  const onSlotGambleOpenChangeRef = useRef(onSlotGambleOpenChange);
  onSlotGambleOpenChangeRef.current = onSlotGambleOpenChange;
  const onSlotCanvasPopupChangeRef = useRef(onSlotCanvasPopupChange);
  onSlotCanvasPopupChangeRef.current = onSlotCanvasPopupChange;
  const onSlotLayoutBoundsRef = useRef(onSlotLayoutBounds);
  useEffect(() => {
    onSlotLayoutBoundsRef.current = onSlotLayoutBounds;
  }, [onSlotLayoutBounds]);
  const onSceneCompleteRef = useRef(onSceneComplete);
  const onOpenSettingsRef = useRef(onOpenSettings);
  const slotSettingsHostRef = useRef(slotSettingsHost);
  slotSettingsHostRef.current = slotSettingsHost;
  const onSlotSceneReadyRef = useRef(onSlotSceneReady);
  const onSlotServerModeChangeRef = useRef(onSlotServerModeChange);
  onSlotServerModeChangeRef.current = onSlotServerModeChange;
  const onSceneShownRef = useRef(onSceneShown);
  onSceneShownRef.current = onSceneShown;
  useLayoutEffect(() => {
    slotSpinFnRef.current = slotSpinFn;
  }, [slotSpinFn]);
  useLayoutEffect(() => {
    onSlotSpinFiredRef.current = onSlotSpinFired;
  }, [onSlotSpinFired]);
  useLayoutEffect(() => {
    onSceneCompleteRef.current = onSceneComplete;
  }, [onSceneComplete]);
  useLayoutEffect(() => {
    onOpenSettingsRef.current = onOpenSettings;
  }, [onOpenSettings]);
  useLayoutEffect(() => {
    onSlotSceneReadyRef.current = onSlotSceneReady;
  }, [onSlotSceneReady]);
  const onLoadoutTileClickRef = useRef(onLoadoutTileClick);
  useLayoutEffect(() => {
    onLoadoutTileClickRef.current = onLoadoutTileClick;
  }, [onLoadoutTileClick]);
  const onBoardStopPressedRef = useRef(onBoardStopPressed);
  useLayoutEffect(() => {
    onBoardStopPressedRef.current = onBoardStopPressed;
  }, [onBoardStopPressed]);
  const onBoardCollectPressedRef = useRef(onBoardCollectPressed);
  useLayoutEffect(() => {
    onBoardCollectPressedRef.current = onBoardCollectPressed;
  }, [onBoardCollectPressed]);
  const onSlotIdleRef = useRef(onSlotIdle);
  useLayoutEffect(() => {
    onSlotIdleRef.current = onSlotIdle;
  }, [onSlotIdle]);
  const onInfoModalToggleRef = useRef(onInfoModalToggle);
  useLayoutEffect(() => {
    onInfoModalToggleRef.current = onInfoModalToggle;
  }, [onInfoModalToggle]);
  // Keep a ref to the currently mounted SlotDemoScene so the loadout-
  // state effects can push updates without re-creating the scene.
  const slotSceneInstanceRef = useRef<SlotDemoScene | null>(null);
  // A session that arrives after the slot opened: switch it to server
  // spins now if it's idle (else it switches at the start of its next spin).
  useLayoutEffect(() => {
    if (slotSpinFn) slotSceneInstanceRef.current?.refreshSpinMode();
  }, [slotSpinFn]);
  // Latest position-bridge anchors — so a freshly-built scene picks them
  // up even if the React pill reported them before the async build landed.
  const walletAnchorsRef = useRef(walletAnchors);
  // Read by the theme preload below without re-running it on scene changes.
  const sceneRef = useRef(scene);
  sceneRef.current = scene;

  // Phase 1 — preload the theme's bundle (textures + audio). The
  // AssetLoader / AudioBus singletons cache aggressively, so a
  // re-run with the same theme resolves immediately. Crucially this
  // runs without touching Pixi: a StrictMode unmount→remount during
  // a slow video load just toggles loadState, no WebGL contexts
  // get created or torn down mid-flight.
  useEffect(() => {
    let cancelled = false;
    setLoadState('loading');
    setLoadProgress(sceneRef.current === 'slot' ? 0 : null);
    void (async () => {
      try {
        // A slot only needs its first-spin textures to become playable; the
        // slot scene fetches the rest of the theme and its audio once it's
        // built, so they don't compete with Pixi's renderer code on a slow
        // line. Other scenes keep the full blocking load.
        if (sceneRef.current === 'slot') {
          await AssetLoader.shared.loadThemeEssentials(theme, (loaded, total) => {
            if (!cancelled) setLoadProgress(total === 0 ? 1 : loaded / total);
          });
        } else {
          await Promise.all([
            AssetLoader.shared.loadTheme(theme),
            AudioBus.shared.loadTheme(theme),
          ]);
        }
        if (!cancelled) {
          // Mark WHICH theme is ready before flipping loadState, so the
          // build effect's {@code readyThemeId === theme.id} guard sees
          // a matching pair in the same commit.
          setReadyThemeId(theme.id);
          setLoadState('ready');
        }
      } catch (err) {
        if (cancelled) return;
        console.error('[CanvasHost] preload failed', err);
        setLoadState('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [theme]);

  // Phase 2 — construct the Pixi scene once assets are ready. This
  // useEffect waits for loadState='ready' so {@code app.init()} +
  // the scene's own preload (which is now a no-op in the warm-cache
  // case) run in a brief, predictable window.
  useEffect(() => {
    // Gate on BOTH the ready flag and that the ready theme is the one
    // we're currently rendering — see {@link readyThemeId}. Prevents a
    // theme-change from rebuilding the scene against not-yet-loaded
    // assets (glyph/gradient fallbacks that never recover).
    if (loadState !== 'ready' || readyThemeId !== theme.id) return undefined;
    const container = containerRef.current;
    if (!container) return undefined;

    let instance: PixiScene;
    switch (scene) {
      case 'wheel':
        instance = new WheelDemoScene(container, theme, profile);
        break;
      case 'bingo':
        instance = new BingoDemoScene(container, theme, profile);
        break;
      case 'board':
        instance = new BoardDemoScene(container, theme, profile);
        break;
      case 'pickem':
        instance = new PickEmDemoScene(container, theme, profile);
        break;
      case 'crash':
        instance = new CrashDemoScene(container, theme, profile);
        break;
      case 'plinko':
        instance = new PlinkoDemoScene(container, theme, profile);
        break;
      case 'higherlower':
        instance = new HigherLowerDemoScene(container, theme, profile);
        break;
      case 'scratch':
        instance = new ScratchDemoScene(container, theme, profile, {
          onComplete: () => onSceneCompleteRef.current?.(),
        });
        break;
      case 'chest':
        instance = new ChestOpenScene(container, theme, profile, {
          onComplete: () => onSceneCompleteRef.current?.(),
        });
        break;
      case 'daily':
        instance = new DailyQuestScene(container, theme, profile, {
          onComplete: () => onSceneCompleteRef.current?.(),
        });
        break;
      case 'slot':
      default: {
        // Adapter forwards each spin to whatever the latest ref value
        // is — so callers can update their callback freely without
        // disturbing the canvas.
        //
        // A null slotSpinFn must play local spins: with no server
        // session (e.g. /v1/players 404s) a server spin can only fail,
        // which left the standalone slot unplayable (producer audit, May
        // 2026). serverSpinAvailable gates the adapter on the latest ref,
        // so the scene stays local while it is null and switches to
        // server spins between spins once it arrives (a first visit on a
        // slow connection, or the wheel Reel Race once JOIN_ROOM resolves
        // the seat).
        const spinFnAdapter: SlotSpinFn = (bet: number) => {
          const fn = slotSpinFnRef.current;
          if (!fn) return Promise.reject(new Error('spinFn unavailable'));
          return fn(bet);
        };
        const onSpinFiredAdapter = (bet: number) => {
          onSlotSpinFiredRef.current?.(bet);
        };
        const gambleFnAdapter: SlotGambleFn = (req) => {
          const fn = slotGambleFnRef.current;
          if (!fn) return Promise.reject(new Error('gambleFn unavailable'));
          return fn(req);
        };
        const slotInstance = new SlotDemoScene(container, theme, profile, {
          spinFn: spinFnAdapter,
          gambleFn: gambleFnAdapter,
          baseBet: slotBetLadder ? slotBetFor(theme.id) : null,
          serverSpinAvailable: () => slotSpinFnRef.current != null,
          onServerModeChange: () => onSlotServerModeChangeRef.current?.(true),
          onSpinFired: onSpinFiredAdapter,
          onServerSpinLanded: (r) => onSlotServerSpinLandedRef.current?.(r),
          onGambleOpenChange: (open) => onSlotGambleOpenChangeRef.current?.(open),
          onCanvasPopupChange: (open) => onSlotCanvasPopupChangeRef.current?.(open),
          onboardingSession: onboardingSession ?? null,
          onOpenSettings: () => onOpenSettingsRef.current?.(),
          // The standalone slot draws its own settings, level-ups and finds;
          // what only the host can do is read when the gear is tapped.
          settingsHost: slotSettingsHostRef.current
            ? {
                get push() {
                  return slotSettingsHostRef.current?.push ?? null;
                },
                get restore() {
                  return slotSettingsHostRef.current?.restore ?? null;
                },
                get story() {
                  return slotSettingsHostRef.current?.story ?? null;
                },
                get account() {
                  return slotSettingsHostRef.current?.account ?? null;
                },
              }
            : null,
          celebrateOnCanvas: slotSettingsHostRef.current !== undefined,
          gambleEnabled,
          onLayoutBounds: (b) => onSlotLayoutBoundsRef.current?.(b),
          showLoadoutStrip,
          onLoadoutTileClick: (idx) => onLoadoutTileClickRef.current?.(idx),
          onInfoModalToggle: (open) => onInfoModalToggleRef.current?.(open),
          slotNodeId: slotNodeId != null ? String(slotNodeId) : null,
          road,
          onBoardStopPressed: () => onBoardStopPressedRef.current?.(),
          onBoardCollectPressed: () => onBoardCollectPressedRef.current?.(),
          onIdle: () => onSlotIdleRef.current?.(),
        });
        // Hand the slot-scene reference up to the host so it can call
        // {@link SlotDemoScene#playItemDropReveal} on Reel-Race item
        // drops. Fired again with null in the cleanup below.
        slotSceneInstanceRef.current = slotInstance;
        // Seed the position-bridge targets (the pill may have measured
        // before this async scene build completed).
        slotInstance.setWalletAnchors(walletAnchorsRef.current ?? null);
        onSlotSceneReadyRef.current?.(slotInstance);
        // Dev introspection — expose the live slot scene so the preview
        // harness can poke at internal HUD state without React
        // instrumentation. Mirrors WheelResolveCanvas's __wheelScene.
        (window as unknown as { __slotScene?: unknown }).__slotScene = slotInstance;
        instance = slotInstance;
        break;
      }
    }

    let disposed = false;
    setSceneShown(false);
    onSceneShownRef.current?.(false);
    void instance
      .init()
      .then(() => {
        if (disposed) return;
        setSceneShown(true);
        onSceneShownRef.current?.(true);
      })
      .catch((err: unknown) => {
        console.error('[CanvasHost] scene.init failed', err);
      });

    return () => {
      disposed = true;
      onSceneShownRef.current?.(false);
      if (instance instanceof SlotDemoScene) {
        slotSceneInstanceRef.current = null;
        onSlotSceneReadyRef.current?.(null);
      }
      instance.destroy();
    };
    // onSceneComplete is intentionally absent — the adapter above
    // reads onSceneCompleteRef.current at call time so a parent
    // re-render minting a new arrow doesn't tear down the scene.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadState, readyThemeId, scene, theme, profile, onboardingSession, showLoadoutStrip, slotNodeId, road]);

  // Push loadout strip state changes to the mounted slot scene
  // without re-creating the scene (the slot scene's strip subscribes
  // to setter calls; identity changes are fine, content drives the
  // Pixi-side rendering).
  useLayoutEffect(() => {
    if (!loadoutSlots) return;
    slotSceneInstanceRef.current?.setLoadoutSlots(loadoutSlots);
  }, [loadoutSlots]);
  useLayoutEffect(() => {
    slotSceneInstanceRef.current?.setLoadoutPending(
      loadoutPendingIcon ?? null,
      loadoutPendingLabel ?? '',
    );
  }, [loadoutPendingIcon, loadoutPendingLabel]);
  useLayoutEffect(() => {
    if (loadoutHidden === undefined) return;
    slotSceneInstanceRef.current?.setLoadoutHidden(loadoutHidden);
  }, [loadoutHidden]);
  // Position bridge — keep the ref fresh (for the creation-time seed) and
  // push the live coin/gem chip centres to the running scene.
  useLayoutEffect(() => {
    walletAnchorsRef.current = walletAnchors;
    slotSceneInstanceRef.current?.setWalletAnchors(walletAnchors ?? null);
  }, [walletAnchors]);

  return (
    <div className="relative block h-full w-full">
      <div ref={containerRef} className="absolute inset-0" />
      {loadState !== 'error' && (loadState === 'loading' || !sceneShown) ? (
        <SceneLoadingOverlay
          name={theme.name}
          accent={theme.palette.accent}
          secondary={theme.palette.secondary}
          progress={loadState === 'ready' ? 1 : loadProgress}
        />
      ) : null}
      {loadState === 'error' ? (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-xs font-medium tracking-wide text-rose-400/80">
          failed to load. Refresh the page
        </div>
      ) : null}
    </div>
  );
}
